import { z } from 'zod';
import type { LocalizedText } from '../permissions.ts';
import type { ThemeMenu } from '../theme.ts';

/**
 * Layouts do Estúdio de estilos (ADR 0128): onde fica o menu e como lista e item aparecem. Cada
 * layout tem os próprios ajustes; os valores possíveis de cada ajuste ficam numa lista só.
 */
export const LAYOUT_OPTION_VALUES = {
  density: ['compact', 'comfortable', 'spacious'],
  header: ['simple', 'band', 'compact'],
  filters: ['bar', 'chips', 'facets'],
  kpi: ['card', 'strip', 'none'],
  recordOpen: ['page', 'drawer', 'modal'],
  pageWidth: ['full', 'contained'],
  submenu: ['accordion', 'flyout', 'inline'],
  navLabels: ['icons', 'text'],
  subnav: ['strip', 'dropdown'],
  listWidth: ['narrow', 'medium', 'wide'],
  listItem: ['card', 'row'],
  statusBar: ['show', 'hide'],
  tiles: ['cards', 'icons'],
  steps: ['show', 'hide'],
  contentWidth: ['narrow', 'wide'],
  topLinks: ['show', 'hide'],
  ribbon: ['full', 'compact'],
  rowNumbers: ['show', 'hide'],
} as const;
export type LayoutOptionKey = keyof typeof LAYOUT_OPTION_VALUES;
export type LayoutOptions = {
  [K in LayoutOptionKey]: (typeof LAYOUT_OPTION_VALUES)[K][number];
};

/** O primeiro valor de cada ajuste é o padrão. */
export const LAYOUT_OPTION_DEFAULTS = Object.fromEntries(
  Object.entries(LAYOUT_OPTION_VALUES).map(([k, v]) => [k, v[0]]),
) as LayoutOptions;

export const layoutOptionsSchema = z
  .object(
    Object.fromEntries(
      Object.entries(LAYOUT_OPTION_VALUES).map(([k, v]) => [
        k,
        z.enum(v as unknown as [string, ...string[]]).optional(),
      ]),
    ),
  )
  .strip() as unknown as z.ZodType<Partial<LayoutOptions>>;

export interface LayoutSpec {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  /** Os ajustes que fazem sentido neste layout (os outros ficam no padrão e não aparecem). */
  options: readonly LayoutOptionKey[];
  /**
   * O menu que a moldura de hoje desenha enquanto este layout não tem moldura própria
   * (fases 2 e 3 do ADR 0128).
   */
  menu: ThemeMenu;
}

const L = (
  id: string,
  pt: [string, string],
  en: [string, string],
  menu: ThemeMenu,
  options: LayoutOptionKey[],
): LayoutSpec => ({
  id,
  name: { 'pt-BR': pt[0], en: en[0] },
  description: { 'pt-BR': pt[1], en: en[1] },
  menu,
  options: [...options, 'density'],
});

export const LAYOUTS: readonly LayoutSpec[] = [
  L(
    'classic',
    ['Lateral clássica', 'Menu à esquerda com áreas e submenus. Busca no topo.'],
    ['Classic side menu', 'Menu on the left with areas and submenus. Search on top.'],
    'side',
    ['submenu', 'navLabels', 'header', 'filters', 'kpi', 'recordOpen', 'pageWidth'],
  ),
  L(
    'dual',
    ['Trilho e coluna', 'Áreas num trilho fino e, ao lado, as telas e as visões salvas.'],
    ['Rail and column', 'Areas in a thin rail, with screens and saved views beside it.'],
    'side',
    ['navLabels', 'header', 'filters', 'kpi', 'recordOpen'],
  ),
  L(
    'top',
    ['Topo com faixa', 'Áreas numa faixa no topo e as telas da área logo abaixo.'],
    ['Top bar', 'Areas in a top bar and the area screens right below.'],
    'top',
    ['subnav', 'navLabels', 'header', 'filters', 'kpi', 'recordOpen', 'pageWidth'],
  ),
  L(
    'split',
    ['Mestre e detalhe', 'Lista à esquerda e o item escolhido à direita.'],
    ['Master and detail', 'List on the left and the chosen item on the right.'],
    'side',
    ['listWidth', 'listItem', 'navLabels'],
  ),
  L(
    'tabs',
    ['Abas de trabalho', 'Cada tela ou item aberto vira uma aba.'],
    ['Work tabs', 'Each open screen or item becomes a tab.'],
    'top',
    ['statusBar', 'header', 'filters', 'kpi'],
  ),
  L(
    'launcher',
    ['Blocos de áreas', 'O início é uma grade de áreas; dentro, só as telas dela.'],
    ['Area tiles', 'Home is a grid of areas; inside, only its screens.'],
    'top',
    ['tiles', 'steps', 'kpi'],
  ),
  L(
    'focus',
    ['Foco e comando', 'Quase sem moldura; tudo pela busca de comandos.'],
    ['Focus and command', 'Almost no frame; everything through the command search.'],
    'top',
    ['contentWidth', 'topLinks', 'filters'],
  ),
  L(
    'sheet',
    ['Planilha', 'Faixa de botões no topo, grade de células e totais embaixo.'],
    ['Spreadsheet', 'Button ribbon on top, a cell grid and totals below.'],
    'top',
    ['ribbon', 'rowNumbers'],
  ),
  L(
    'inspector',
    ['Três colunas', 'Menu, lista e um painel com o item escolhido.'],
    ['Three columns', 'Menu, list and a panel with the chosen item.'],
    'side',
    ['submenu', 'navLabels', 'header', 'filters'],
  ),
];
export const LAYOUT_IDS = LAYOUTS.map((l) => l.id) as [string, ...string[]];

export function layoutSpec(id: string): LayoutSpec | undefined {
  return LAYOUTS.find((l) => l.id === id);
}

/** Os ajustes do layout com os padrões preenchidos. Ajuste que não é do layout fica no padrão. */
export function resolvedLayoutOptions(id: string, options?: Partial<LayoutOptions>): LayoutOptions {
  const spec = layoutSpec(id);
  const out = { ...LAYOUT_OPTION_DEFAULTS };
  if (!spec || !options) return out;
  for (const k of spec.options) {
    const v = options[k];
    if (v !== undefined) (out as Record<string, string>)[k] = v;
  }
  return out;
}

/** Menu no celular, igual para todo layout. */
export const MOBILE_NAVS = ['bottom', 'drawer', 'grid'] as const;
export type MobileNav = (typeof MOBILE_NAVS)[number];
